import { api } from "./client";
import type { ApiResponse } from "./types";

// ─── Shapes ─────────────────────────────────────────────────────────
// Mirrors apps/api/src/modules/search/search.service.ts return maps.
//
// `type` is a STRING, not a union, deliberately. The server just added ten
// values to it and will add more as new kinds of content become findable.
// Narrowing it here would mean a kind we have not heard of blanks the row
// instead of rendering it — which is the exact breakage the API avoided by
// keeping the original four values working.

export interface SearchResult {
  type: string;
  id: string;
  title: string;
  subtitle?: string;
  description?: string;
  imageUrl?: string;
  url: string;
  meta?: Record<string, unknown>;
  /** How it was found: "spelling", "meaning", or both. Meaning alone is a neighbour, not a match. */
  matchedBy?: string[];
}

/**
 * How many of each kind survived the visibility checks.
 *
 * An open map, because the server adds keys. The four original ones are always
 * present; read the rest defensively.
 */
export type SearchCounts = Record<string, number>;

export interface SearchResponse {
  results: SearchResult[];
  counts: SearchCounts;
  query: string;
  category: string;
  /**
   * The id this search was recorded under.
   *
   * Send it to `analytics.trackSearchClick` with whichever result was opened.
   * That is the whole of how ranking becomes measurable: without a click, the
   * only evidence about whether the first result was the right one is somebody
   *'s opinion. Null when the query was too short to run.
   */
  searchId: string | null;
  /**
   * Words in the query that appear in NO indexed document.
   *
   * The honest half of a result list. A search for a trade nobody offers still
   * returns near neighbours — "wedding photographer" comes back with a decor
   * firm and a bridal salon — so the count alone can never show a gap. These
   * are the words we could not answer.
   *
   * Nothing is hidden because of this. Show it ABOVE the results, not instead
   * of them.
   */
  unmatchedTerms: string[];
}

/** The categories the endpoint accepts. Matches the published description. */
export const SEARCH_CATEGORIES = [
  "all",
  "providers",
  "shop_items",
  "events",
  "questions",
  "voices",
  "users",
  "institutions",
  "organizers",
  "haats",
  "nostalgia",
  "heritage",
] as const;

export interface SearchParams {
  q: string;
  category?: string;
  city?: string;
  state?: string;
  limit?: number;
}

/**
 * Search everything.
 *
 * Public — it works signed out, and it SHOULD be exercised both ways. A signed
 * -in caller sees seeded organisers they can claim; a signed-out one does not,
 * and that difference is enforced when results are built rather than when they
 * are matched.
 */
export function search(params: SearchParams) {
  const qs = new URLSearchParams();
  qs.set("q", params.q);
  if (params.category && params.category !== "all") qs.set("category", params.category);
  if (params.city) qs.set("city", params.city);
  if (params.state) qs.set("state", params.state);
  qs.set("limit", String(params.limit ?? 20));
  return api.get<ApiResponse<SearchResponse>>(`/search?${qs.toString()}`);
}

export interface Suggestion {
  type: string;
  id: string;
  text: string;
  url?: string;
}

/** Autocomplete, for the type-ahead. */
export function suggestions(q: string, limit = 5) {
  return api.get<ApiResponse<Suggestion[]>>(
    `/search/suggestions?q=${encodeURIComponent(q)}&limit=${limit}`,
  );
}
